/**
 * Computer-controlled racers.
 *
 * Bots run the same arcade physics as a person — they only differ in where the
 * `KartInput` comes from. The driver is a lookahead follower with a corner
 * budget: aim at a point a speed-dependent DISTANCE along the centerline, steer
 * at it, and hold a target speed set by how much the road turns within braking
 * range. Boost goes down the straights, the handbrake comes out for genuinely
 * tight corners, and a bot that has buried itself in the armco reverses out
 * instead of grinding there for the rest of the race.
 *
 * Distances are in world units rather than centerline samples, because sample
 * spacing differs per circuit — a fixed sample count is a different amount of
 * road on every map, which is how the tight circuits end up undriveable.
 *
 * Only the lobby host simulates bots; it relays their transforms through the
 * server like any other player, so every client sees the same field.
 *
 * Skill (0..1) scales lookahead, cornering confidence, steering accuracy and
 * how greedily the boost is used, which is enough to make a grid feel varied
 * without needing separate behaviours. The lobby's bot-skill setting picks the
 * band the whole field races in (see shared/protocol.ts).
 */
import * as THREE from 'three';

import { stepKart } from './physics';
import { createVehicle } from './vehicles';
import { getVehicle } from './vehicles';
import type { KartInput, KartState, TrackData, VehicleStats, VehicleVisual } from './types';
import { passedGate } from './track';
import { createKart } from './physics';
import { LapTracker } from './progress';

/** One bot: physics state, its car, and where it is round the loop. */
export interface Bot {
  id: string;
  state: KartState;
  visual: VehicleVisual;
  stats: VehicleStats;
  skill: number;
  /** Accumulated wheel rotation, in radians. */
  spin: number;
  /**
   * Laps and gates, under exactly the rules the local player races by. Bots used
   * to test only the gate they expected next, which meant one missed gate parked
   * them for the rest of the race — a bot that ran wide at Canyon Dash's pinch
   * drove the remaining laps without its progress ever moving again.
   */
  progress: LapTracker;
  /** Position at the previous gate check, so gates are tested over the step. */
  lastPos: THREE.Vector3;
  /** Smoothed steering, so the front wheels do not twitch. */
  steer: number;
  /** Seconds of boost still queued up. */
  boostFor: number;
  /** How long this bot has been going nowhere — the trigger for a recovery. */
  stuckFor: number;
  /** Seconds left of backing out of whatever it drove into. */
  reverseFor: number;
  /** Where the bot is around the centerline, carried forward frame to frame. */
  lineIndex: number;
}

const _toAim = new THREE.Vector3();
const _forward = new THREE.Vector3();
const _aim = new THREE.Vector3();

/**
 * How far either side of last frame's index to look. A kart covers at most ~4
 * units per step (fastest vehicle, full boost, against the dt clamp) and samples
 * are a few units apart, so this is generous by an order of magnitude.
 */
const INDEX_WINDOW = 24;
/** Beyond this from the tracked sample, assume we lost it and rescan. */
const INDEX_LOST = 45;

/**
 * Where the bot is around the centerline, searched near where it was last frame.
 *
 * A global nearest-sample scan looks equivalent and is not: circuits fold back
 * on themselves, and at the far end of a hairpin the nearest sample can flip to
 * the other side of the fold. The aim point then teleports half a corner away
 * and the bot chases it — on Canyon Dash a fast enough bot would drive back and
 * forth across the fold for the entire race without ever reaching gate 2.
 * Following the index forward makes that impossible, and costs less besides.
 */
function trackIndex(track: TrackData, at: THREE.Vector3, previous: number): number {
  const line = track.centerline;
  const n = line.length;
  let best = previous;
  let bestD2 = Infinity;
  for (let k = -INDEX_WINDOW; k <= INDEX_WINDOW; k++) {
    const i = (((previous + k) % n) + n) % n;
    const dx = line[i].x - at.x;
    const dz = line[i].z - at.z;
    const d2 = dx * dx + dz * dz;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = i;
    }
  }
  // Only if the kart is nowhere near the stretch we were following — a spawn, or
  // a shove clean across the circuit — is the whole loop worth searching.
  return bestD2 <= INDEX_LOST * INDEX_LOST ? best : nearestIndex(track, at);
}

/** Index of the centerline sample nearest a position, searching all of them. */
function nearestIndex(track: TrackData, at: THREE.Vector3): number {
  const line = track.centerline;
  let best = 0;
  let bestD2 = Infinity;
  for (let i = 0; i < line.length; i++) {
    const dx = line[i].x - at.x;
    const dz = line[i].z - at.z;
    const d2 = dx * dx + dz * dz;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = i;
    }
  }
  return best;
}

/**
 * How far the nose has to swing to point at `target`, in radians, wrapped to
 * (-pi, pi].
 *
 * The result is in STEER units, not "angle to the left": the physics integrates
 * `heading += handling * steer * dt`, so feeding this straight back as `steer`
 * turns toward the target. Getting that sign backwards is silent — the bot
 * steers away from its own aim point, runs out of road at the first corner and
 * spends the race grinding along the armco — so bots.test.ts drives a full lap
 * on every circuit rather than trusting the reasoning.
 */
function bearing(from: THREE.Vector3, heading: number, target: THREE.Vector3): number {
  _toAim.copy(target).sub(from);
  _toAim.y = 0;
  const targetAngle = Math.atan2(_toAim.x, _toAim.z);
  let delta = targetAngle - heading;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  return delta;
}

/**
 * Tightest turn radius the road reaches within `distance` ahead, in world units;
 * Infinity for a straight.
 *
 * Radius, not total turn. Summing how far the road swings round is the obvious
 * measure and the wrong one: a long constant sweep adds up to a great deal of
 * turning and can still be taken flat out, so a bot driven off the sum crawls
 * round every fast curve on the circuit. What actually caps speed is the
 * tightest point — a kart turning at `handling * speedFactor` rad/s can hold a
 * radius of v / omega and no less.
 *
 * Measured over a stencil a few samples wide, since a single segment-to-segment
 * angle at this sampling density is mostly noise.
 */
const RADIUS_STENCIL = 3;

function tightestRadiusAhead(
  line: THREE.Vector3[],
  index: number,
  spacing: number,
  distance: number,
): number {
  const n = line.length;
  const steps = Math.max(1, Math.min(n - 1, Math.round(distance / spacing)));
  let tightest = Infinity;
  for (let k = 0; k <= steps; k++) {
    const a = line[(index + k) % n];
    const b = line[(index + k + RADIUS_STENCIL) % n];
    const c = line[(index + k + RADIUS_STENCIL * 2) % n];
    let delta =
      Math.atan2(c.x - b.x, c.z - b.z) - Math.atan2(b.x - a.x, b.z - a.z);
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    const turn = Math.abs(delta);
    if (turn < 1e-4) continue; // straight enough to be no constraint at all
    const arc = a.distanceTo(b) + b.distanceTo(c);
    const radius = arc / turn;
    if (radius < tightest) tightest = radius;
  }
  return tightest;
}

/**
 * Decide what a bot does this frame.
 *
 * Two questions, answered separately: where to point (the aim point), and how
 * fast the road ahead allows (the corner budget). Keeping them apart is what
 * stops a bot from arriving at a hairpin flat out and understeering into the
 * barrier, which no amount of steering gain can save.
 */
export function driveBot(bot: Bot, track: TrackData, dt: number): KartInput {
  const line = track.centerline;
  const n = line.length;
  const index = trackIndex(track, bot.state.position, bot.lineIndex);
  bot.lineIndex = index;
  const spacing = Math.max(0.5, line[index].distanceTo(line[(index + 1) % n]));

  const speed = Math.abs(bot.state.speed);
  const speed01 = Math.min(1, speed / bot.stats.maxSpeed);

  // Aim a real distance up the road, not a fixed number of samples: circuits
  // are sampled at different spacings, and the tight ones are exactly where
  // being a few metres short of the apex costs the corner.
  const aimDistance = 10 + speed * 0.75 + bot.skill * 6;
  const aimSteps = Math.max(2, Math.round(aimDistance / spacing));
  const aimIndex = (index + aimSteps) % n;
  _aim.copy(line[aimIndex]);

  // The tightest thing coming up, over roughly the distance it takes to slow
  // down from here. Skill lengthens the window, because that is what actually
  // separates quick drivers: seeing the corner sooner lets them slow for it
  // earlier and therefore commit harder once in it. Being braver over a short
  // window just means arriving too fast and running wide.
  const radius = tightestRadiusAhead(line, index, spacing, 11 + speed * (0.7 + bot.skill * 0.9));

  const steerTo = bearing(bot.state.position, bot.state.heading, _aim);

  // Recovery: buried in the armco, or spun to a halt. Back off, turning the
  // nose back toward the road as it goes. Steering inverts in reverse, so the
  // sign flips with it.
  bot.stuckFor = speed < 2.5 ? bot.stuckFor + dt : 0;
  if (bot.reverseFor > 0) {
    bot.reverseFor -= dt;
    bot.steer = steerTo >= 0 ? -1 : 1;
    return { throttle: -1, steer: bot.steer, boost: false, drift: false };
  }
  if (bot.stuckFor > 0.7) {
    bot.stuckFor = 0;
    bot.reverseFor = 0.85;
  }

  // Steering: proportional, clamped, smoothed. Weaker bots react later and
  // overshoot, which reads as scruffy driving rather than as a bug.
  const gain = 1.7 + bot.skill * 1.1;
  const target = Math.max(-1, Math.min(1, steerTo * gain));
  bot.steer += (target - bot.steer) * (1 - Math.exp(-dt * (7 + bot.skill * 9)));

  // Speed budget. The kart turns at `handling * speedFactor` rad/s, so holding a
  // radius R needs v <= R * omega — which is the corner's property, not the
  // driver's. Skill is `commit`: how close to that the driver actually runs, and
  // how much of the car they are willing to use where nothing is bending at all.
  //
  // Keeping those two apart is what stopped a quick bot believing a hairpin was
  // takeable at half its top speed. It is not: it understeers straight across
  // the apex, and at Canyon Dash's pinch that means driving round the outside of
  // a checkpoint, every lap, forever.
  const commit = 0.5 + bot.skill * 0.44;
  // Measured against the top speed available RIGHT NOW. Judging it against the
  // unboosted figure makes the boost self-defeating: the kart passes its own
  // target the moment the afterburner lights and the driver lifts straight off
  // it, so the whole burst is spent coasting.
  const boosting = bot.boostFor > 0;
  const topSpeed = bot.stats.maxSpeed * (boosting ? bot.stats.boostMul : 1);
  // 0.75 stands in for the mid-range of the physics' speedFactor; the corner is
  // entered a little under the limit rather than exactly on it. Grip scales it
  // because the turn rate is only half the story — a slippery kart put on the
  // same line at the same speed simply slides off the outside of it.
  const corneringSpeed = radius * bot.stats.handling * 0.75 * (0.62 + bot.stats.grip * 0.42);
  const targetSpeed = Math.min(topSpeed, corneringSpeed) * commit;

  // Braking is all-or-nothing in the physics, so only reach for it when
  // genuinely carrying too much speed; otherwise lift and coast.
  let throttle = 1;
  if (bot.state.speed > targetSpeed * 1.12) throttle = -1;
  else if (bot.state.speed > targetSpeed) throttle = 0;

  // Boost is judged over a fixed stretch of road rather than the braking
  // window: the question is "is there a straight here", and that must not get
  // harder to answer just because the kart is already going quickly.
  //
  // The meter regenerates at 0.2/s and drains at 0.5/s, so roughly a third of a
  // lap can be spent on boost. A rookie sits on a full meter waiting for a
  // certainty and never spends that third; an ace fires it at every kink and
  // runs the meter permanently near empty, which is most of the difference
  // between the top two tiers.
  bot.boostFor -= dt;
  const openAhead = tightestRadiusAhead(line, index, spacing, 60);
  if (
    bot.boostFor <= 0 &&
    openAhead > 90 - bot.skill * 45 &&
    Math.abs(steerTo) < 0.1 + bot.skill * 0.14 &&
    bot.state.boostMeter > 0.9 - bot.skill * 0.8
  ) {
    bot.boostFor = 0.6 + bot.skill * 1.2;
  }
  // Never carry a burst into a corner the driver cannot hold it through. The
  // ceiling matters more than the floor here: a bot that boosts into a hairpin
  // spends the lap bouncing off the armco, which is slower than never boosting.
  if (radius < 34 - bot.skill * 8) bot.boostFor = 0;

  return {
    throttle,
    steer: bot.steer,
    boost: bot.boostFor > 0,
    // Handbrake only for genuinely tight corners taken at speed, and only by
    // drivers good enough to gather the slide up again afterwards.
    drift: radius < 16 && speed01 > 0.7 && bot.skill > 0.6,
  };
}

/** Build a bot on a grid slot. `skill` is 0 (clumsy) .. 1 (quick). */
export function createBot(
  id: string,
  vehicleId: string,
  position: THREE.Vector3,
  heading: number,
  skill: number,
  track: TrackData,
  totalLaps: number,
): Bot {
  const visual = createVehicle(vehicleId);
  const state = createKart(position.clone(), heading);
  visual.group.position.copy(state.position);
  visual.group.quaternion.copy(state.bodyQuat);
  return {
    id,
    state,
    visual,
    stats: getVehicle(vehicleId),
    skill,
    lineIndex: nearestIndex(track, state.position),
    spin: 0,
    progress: new LapTracker(track.checkpoints.length, totalLaps),
    lastPos: state.position.clone(),
    steer: 0,
    boostFor: 0,
    stuckFor: 0,
    reverseFor: 0,
  };
}

/**
 * Advance one bot: drive, simulate, and track its progress round the loop under
 * exactly the rules the local player races by. Returns true on the frame it
 * crosses the finish line.
 */
export function stepBot(bot: Bot, track: TrackData, dt: number): boolean {
  if (bot.progress.finished) return false;

  stepKart(bot.state, driveBot(bot, track, dt), bot.stats, track, dt);
  bot.visual.group.position.copy(bot.state.position);
  bot.visual.group.quaternion.copy(bot.state.bodyQuat);

  // Every gate, not just the expected one — the same reason the player does it:
  // a gate driven around has to be noticed rather than silently ending progress.
  let justFinished = false;
  for (let c = 0; c < track.checkpoints.length; c++) {
    if (!passedGate(track.checkpoints[c], bot.lastPos, bot.state.position)) continue;
    for (const event of bot.progress.cross(c)) {
      if (event.type === 'finish') justFinished = true;
    }
  }
  bot.lastPos.copy(bot.state.position);

  // Last resort: a bot facing the wrong way against a barrier can reverse for
  // as long as it likes without ever getting the nose round. Once it has been
  // stationary AND pointing backwards for a while, rotate it toward the racing
  // direction — ugly, but it is the difference between a race and a bot parked
  // in the scenery for three laps.
  if (Math.abs(bot.state.speed) < 1.5 && bot.stuckFor > 2) {
    _forward.set(Math.sin(bot.state.heading), 0, Math.cos(bot.state.heading));
    const sample = track.sample(bot.state.position);
    if (sample.forward.dot(_forward) < 0.3) {
      let delta = Math.atan2(sample.forward.x, sample.forward.z) - bot.state.heading;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      bot.state.heading += Math.sign(delta) * dt * 2.2;
    }
  }

  return justFinished;
}
